import clsx from 'clsx';
import { ArrowRight, BriefcaseBusiness, Cake, CalendarDays, CalendarOff, Clock, Megaphone, PartyPopper, Plane, Timer, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { ProcOutput } from '@pz/server';
import { d, kw, wd } from '../lib/format';
import { AnnouncementCard } from './announcements';
import { Button, EmptyState, Panel } from './ui';

export type HomeData = ProcOutput<'home.get'>;

/* ------------------------------------------------------------------ */
/* Shortcut tiles                                                       */
/* ------------------------------------------------------------------ */

export interface Tile {
  key: string;
  icon: ReactNode;
  label: string;
  to?: string;
  onClick?: () => void;
  /** a figure on the tile (open approvals, hours of the week) */
  value?: ReactNode;
  tone?: 'bad' | 'warn';
  primary?: boolean;
}

/**
 * Tiles of the start page: the few things this person does most (the rights decide which tiles exist). A figure on
 * a tile is a live count; red means something to correct.
 */
export function TileGrid({ tiles }: { tiles: Tile[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6">
      {tiles.map((x) => {
        const body = (
          <>
            <span className={clsx('inline-flex size-8 items-center justify-center rounded-md', x.primary ? 'bg-accent-ink/15 text-accent-ink' : 'bg-accent-soft text-accent')}>{x.icon}</span>
            <span className="mt-2 block text-sm leading-tight font-medium">{x.label}</span>
            <span className={clsx('num mt-0.5 block min-h-5 text-sm font-semibold', x.tone === 'bad' ? 'text-bad' : x.tone === 'warn' ? 'text-warn' : x.primary ? 'text-accent-ink' : 'text-graphite')}>{x.value ?? ''}</span>
          </>
        );
        const cls = clsx(
          'block rounded-lg border p-3 text-left transition-colors',
          x.primary ? 'border-accent bg-accent text-accent-ink hover:brightness-110' : 'border-line bg-sheet hover:border-accent/60 hover:bg-accent-soft/40',
        );
        return x.to ? (
          <Link key={x.key} to={x.to} className={cls}>
            {body}
          </Link>
        ) : (
          <button key={x.key} type="button" onClick={x.onClick} className={cls}>
            {body}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Announcements                                                        */
/* ------------------------------------------------------------------ */

export function AnnouncementsPanel({ data }: { data: HomeData }) {
  const { t } = useTranslation();
  const shown = data.announcements.slice(0, 3);
  const more = data.announcements.length - shown.length;
  return (
    <Panel
      title={
        <span className="flex items-center gap-2">
          <Megaphone className="size-4 text-accent" /> {t('home.announcements')}
          {data.unreadAnnouncements > 0 && <span className="num rounded-full bg-accent px-1.5 text-[11px] font-semibold text-accent-ink">{data.unreadAnnouncements}</span>}
        </span>
      }
      actions={
        <Link to="/announcements">
          <Button size="sm" icon={<ArrowRight className="size-3.5" />}>
            {data.canPublish ? t('home.manageAnnouncements') : t('home.allAnnouncements')}
          </Button>
        </Link>
      }
    >
      {shown.length === 0 ? (
        <p className="text-sm text-graphite">{t('home.noAnnouncements')}</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {shown.map((a) => (
            <AnnouncementCard key={`${a.id}:${a.occurrence}`} a={a} compact />
          ))}
          {more > 0 && (
            <Link to="/announcements" className="text-sm text-accent hover:underline">
              {t('home.moreAnnouncements', { count: more })}
            </Link>
          )}
        </div>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* Coming up                                                            */
/* ------------------------------------------------------------------ */

const KIND_ICON: Record<HomeData['upcoming'][number]['kind'], LucideIcon> = {
  leave: Plane,
  holiday: PartyPopper,
  event: CalendarDays,
  closure: CalendarOff,
  deadline: Timer,
  birthday: Cake,
  project_end: BriefcaseBusiness,
  week_deadline: Clock,
};

export function UpcomingPanel({ items, today }: { items: HomeData['upcoming']; today: string }) {
  const { t } = useTranslation();
  const text = (i: HomeData['upcoming'][number]): string => {
    switch (i.kind) {
      case 'leave':
        return `${t(`week.absence.${String(i.params.kind) as 'vacation'}`, { defaultValue: String(i.params.kind) })}${i.params.status === 'requested' ? ` · ${t('home.up.requested')}` : ''}`;
      case 'birthday':
        return t('home.up.birthday', { name: i.title });
      case 'project_end':
        return t('home.up.projectEnd', { project: i.title });
      case 'week_deadline':
        return t('home.up.weekDeadline', { week: kw(String(i.params.week)) });
      case 'holiday':
        return i.title;
      default: {
        // company events: half a day ("am"/"pm") or a time range (request 27 of round 4)
        const time = i.params.time === 'am' || i.params.time === 'pm' ? t(`leave.parts.${i.params.time}`) : i.params.time;
        return time ? `${i.title} · ${time}` : i.title;
      }
    }
  };
  return (
    <Panel title={t('home.upcoming')} bodyClassName="p-0">
      {items.length === 0 ? (
        <div className="p-4">
          <EmptyState>{t('home.upcomingEmpty')}</EmptyState>
        </div>
      ) : (
        <ul className="scroll-box divide-y divide-line/70">
          {items.map((i, idx) => {
            const Icon = KIND_ICON[i.kind];
            const row = (
              <span className="flex items-center gap-3 px-4 py-2">
                <span className={clsx('num w-14 shrink-0 text-xs leading-tight', i.date === today ? 'font-semibold text-accent' : 'text-graphite')}>
                  <span className="block">{i.date === today ? t('common.today') : wd(i.date)}</span>
                  <span className="block">{i.to && i.to !== i.date ? `${d(i.date).slice(0, 6)}–${d(i.to).slice(0, 6)}` : d(i.date).slice(0, 6)}</span>
                </span>
                <Icon className="size-4 shrink-0 text-graphite" aria-hidden />
                <span className="min-w-0 flex-1 text-sm">
                  <span className="block truncate">{text(i)}</span>
                  <span className="block text-[11px] text-faint">{t(`home.upKinds.${i.kind}`)}</span>
                </span>
              </span>
            );
            return <li key={`${i.kind}${i.date}${idx}`}>{i.link ? <Link to={i.link} className="block hover:bg-sheet-2">{row}</Link> : row}</li>;
          })}
        </ul>
      )}
    </Panel>
  );
}
